import React, {createContext, useContext, useState, useMemo, useCallback, useRef} from 'react'
import PropTypes from 'prop-types'

const MAX_TOASTS = 2
const DEFAULT_DURATION = 4000
const ACTION_DURATION = 7000

// Create the context
export const CustomToastContext = createContext()

// Custom hook to use the context
export const useCustomToast = () => useContext(CustomToastContext)

let toastIdCounter = 0

// Provider component
export const CustomToastProvider = ({children}) => {
    const [toasts, setToasts] = useState([])
    const toastsRef = useRef([])

    // Keep ref in sync with state for use in callbacks
    toastsRef.current = toasts

    const removeToast = useCallback((id) => {
        setToasts((prev) => {
            const toast = prev.find((t) => t.id === id)
            if (toast?.onClose) toast.onClose()
            return prev.filter((t) => t.id !== id)
        })
    }, [])

    const showCustomToast = useCallback(
        ({
            message,
            actionLabel,
            onActionClick,
            styles,
            messageStyles,
            containerId,
            toastContainerRef,
            returnFocusElement,
            focusActionOnMount,
            onManualClose,
            duration,
            onClose,
            pauseOnHover,
            variant,
            icon
        }) => {
            const id = ++toastIdCounter
            const resolvedDuration = duration || (actionLabel ? ACTION_DURATION : DEFAULT_DURATION)

            const newToast = {
                id,
                message,
                actionLabel,
                onActionClick,
                styles,
                messageStyles,
                containerId,
                toastContainerRef,
                returnFocusElement,
                focusActionOnMount,
                onManualClose,
                duration: resolvedDuration,
                onClose: onClose || null,
                pauseOnHover: pauseOnHover !== false,
                variant,
                icon
            }

            setToasts((prev) => {
                // Newest toast at the beginning, limit to MAX_TOASTS
                const updated = [newToast, ...prev]
                // Trigger onClose for toasts being removed due to limit
                if (updated.length > MAX_TOASTS) {
                    const removed = updated.slice(MAX_TOASTS)
                    removed.forEach((t) => t.onClose?.())
                }
                return updated.slice(0, MAX_TOASTS)
            })

            return id
        },
        []
    )

    const value = useMemo(
        () => ({
            toasts,
            showCustomToast,
            removeToast
        }),
        [toasts, showCustomToast, removeToast]
    )

    return <CustomToastContext.Provider value={value}>{children}</CustomToastContext.Provider>
}

CustomToastProvider.propTypes = {
    children: PropTypes.node.isRequired
}
